/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetPublishStatusRequest,
  GetPublishStatusSecurity,
} from "../models/operations/getpublishstatus.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildPublishStatusQuery,
  prefetchPublishStatus,
  PublishStatusQueryData,
  queryKeyPublishStatus,
} from "./publishStatus.core.js";
export {
  buildPublishStatusQuery,
  prefetchPublishStatus,
  type PublishStatusQueryData,
  queryKeyPublishStatus,
};

export type PublishStatusQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getPublishStatus plugins
 *
 * @remarks
 * Check whether GitHub publishing is configured and connected for this project.
 */
export function usePublishStatus(
  request?: GetPublishStatusRequest | undefined,
  security?: GetPublishStatusSecurity | undefined,
  options?: QueryHookOptions<PublishStatusQueryData, PublishStatusQueryError>,
): UseQueryResult<PublishStatusQueryData, PublishStatusQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildPublishStatusQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getPublishStatus plugins
 *
 * @remarks
 * Check whether GitHub publishing is configured and connected for this project.
 */
export function usePublishStatusSuspense(
  request?: GetPublishStatusRequest | undefined,
  security?: GetPublishStatusSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    PublishStatusQueryData,
    PublishStatusQueryError
  >,
): UseSuspenseQueryResult<PublishStatusQueryData, PublishStatusQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildPublishStatusQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setPublishStatusData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: PublishStatusQueryData,
): PublishStatusQueryData | undefined {
  const key = queryKeyPublishStatus(...queryKeyBase);

  return client.setQueryData<PublishStatusQueryData>(key, data);
}

export function invalidatePublishStatus(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "plugins", "getPublishStatus", ...queryKeyBase],
  });
}

export function invalidateAllPublishStatus(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "plugins", "getPublishStatus"],
  });
}
